// MomentData IR conformance validation — proves SimulationTopology extends
// MomentData and provides runtime validators for topology and seed JSON.

import type {
  SimulationTopology,
  LaneDef,
  FrameDef,
  ConnectionDef,
  SimulationSeed,
  PlatformEvent,
} from '../types/index.ts';

// ---------------------------------------------------------------------------
// MomentData structural compatibility assertion
// ---------------------------------------------------------------------------
// MomentData IR (from Moment ADR-001) requires: lanes, frames, connections.
// SimulationTopology extends this with eventRouting and branchPredicates.

interface MomentData {
  lanes: LaneDef[];
  frames: FrameDef[];
  connections: ConnectionDef[];
  branchLanes?: LaneDef[];
}

// Type-level assertion: SimulationTopology is assignable to MomentData.
// If this fails to compile, SimulationTopology has drifted from MomentData.
const _typeCheck: MomentData = {} as SimulationTopology;
void _typeCheck;

// Verify FACET extensions exist on SimulationTopology at the type level.
// These assertions use void to suppress unused-type warnings while still
// proving the properties exist on SimulationTopology at compile time.
void (0 as unknown as SimulationTopology['eventRouting']);
void (0 as unknown as SimulationTopology['branchPredicates']);

// ---------------------------------------------------------------------------
// Synthetic event prefixes — Facet's own simulation event namespace.
// ---------------------------------------------------------------------------

export const SYNTHETIC_PREFIXES = [
  'SimAction',
  'SimProcess',
  'SimSaga',
  'SimBranch',
  'SimProjection',
  // ADR-037 reads: Moment's simulator emits a query step as `SimQuery.<name>` (a read of a
  // projection, beside `SimProjection.<name>`); without it every scenario carrying a query node
  // was dropped on load.
  'SimQuery',
  'SimOutput',
  'SimExternal',
  'SimFailure',
] as const;

// ---------------------------------------------------------------------------
// Validation result
// ---------------------------------------------------------------------------

export interface ValidationError {
  path: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

function ok(): ValidationResult {
  return { valid: true, errors: [] };
}

function fail(errors: ValidationError[]): ValidationResult {
  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// validateTopology()
// ---------------------------------------------------------------------------

export function validateTopology(topology: SimulationTopology): ValidationResult {
  const errors: ValidationError[] = [];

  const lanes = topology.lanes ?? [];
  const frames = topology.frames ?? [];
  const connections = topology.connections ?? [];
  const eventRouting = topology.eventRouting ?? {};
  const branchPredicates = topology.branchPredicates ?? {};

  // Collect the real ID namespaces used by Moment-generated topologies:
  // - node IDs   (frame::lane::Name::route — used by eventRouting targets)
  // - frame IDs  (moment-N-… — used as connection sources and branchPredicate keys)
  // - context IDs (ctx-… from lanes.contextId / contextMap — used as connection targets)
  const laneIds = new Set(lanes.map((l: LaneDef) => l.id));
  const frameIds = new Set(frames.map((f) => f.id));
  const contextIds = new Set<string>();
  for (const lane of lanes) {
    if (lane.contextId) contextIds.add(lane.contextId);
  }
  for (const ctx of topology.contextMap?.contexts ?? []) {
    if (ctx.contextId) contextIds.add(ctx.contextId);
  }
  const nodeIds = new Set<string>();

  for (const frame of frames) {
    for (const node of frame.nodes) {
      if (nodeIds.has(node.id)) {
        errors.push({
          path: `frames[${frame.id}].nodes[${node.id}]`,
          message: `Duplicate node ID: ${node.id}`,
        });
      }
      nodeIds.add(node.id);

      // All lanes referenced in nodes must exist
      if (!laneIds.has(node.lane)) {
        errors.push({
          path: `frames[${frame.id}].nodes[${node.id}].lane`,
          message: `Node "${node.id}" references non-existent lane "${node.lane}"`,
        });
      }
    }
  }

  // Connection endpoints live in mixed namespaces in real artifacts:
  // frame → context (main edges), frame → frame (return edges), or
  // node → node (hand-authored topologies). Accept any known ID.
  const isKnownEndpoint = (id: string): boolean =>
    nodeIds.has(id) || frameIds.has(id) || contextIds.has(id);

  for (const conn of connections) {
    if (!isKnownEndpoint(conn.from)) {
      errors.push({
        path: `connections[${conn.from}->${conn.to}].from`,
        message: `Connection references unknown source "${conn.from}" (not a node, frame, or context ID)`,
      });
    }
    if (!isKnownEndpoint(conn.to)) {
      errors.push({
        path: `connections[${conn.from}->${conn.to}].to`,
        message: `Connection references unknown target "${conn.to}" (not a node, frame, or context ID)`,
      });
    }
  }

  // eventRouting values must reference existing node IDs
  for (const [eventType, targets] of Object.entries(eventRouting)) {
    for (const targetId of targets) {
      if (!nodeIds.has(targetId)) {
        errors.push({
          path: `eventRouting[${eventType}]`,
          message: `Event routing for "${eventType}" references non-existent node "${targetId}"`,
        });
      }
    }
  }

  // branchPredicate keys reference node IDs (hand-authored) or frame IDs
  // (Moment-generated branch frames)
  for (const key of Object.keys(branchPredicates)) {
    if (!nodeIds.has(key) && !frameIds.has(key)) {
      errors.push({
        path: `branchPredicates[${key}]`,
        message: `Branch predicate references non-existent node or frame "${key}"`,
      });
    }
  }

  return errors.length === 0 ? ok() : fail(errors);
}

// ---------------------------------------------------------------------------
// validateSeed()
// ---------------------------------------------------------------------------

/**
 * Determines whether an event type is valid for the given topology.
 * Valid means: present in the topology's eventRouting table, OR matching
 * one of Facet's synthetic Sim* prefixes.
 */
function isValidEventType(eventType: string, topologyEventTypes: Set<string>): boolean {
  if (topologyEventTypes.has(eventType)) return true;
  // Check synthetic Sim* pattern
  for (const prefix of SYNTHETIC_PREFIXES) {
    if (eventType.startsWith(`${prefix}.`)) return true;
  }
  return false;
}

function isValidPadr004Envelope(event: PlatformEvent, index: number): ValidationError[] {
  const errors: ValidationError[] = [];
  const path = `events[${index}]`;

  if (!event.eventId) {
    errors.push({ path: `${path}.eventId`, message: 'eventId is required' });
  }
  if (!event.eventType) {
    errors.push({ path: `${path}.eventType`, message: 'eventType is required' });
  }
  if (!event.productSource) {
    errors.push({ path: `${path}.productSource`, message: 'productSource is required' });
  }
  if (!event.sessionId) {
    errors.push({ path: `${path}.sessionId`, message: 'sessionId is required' });
  }
  if (!Array.isArray(event.causationEventIds)) {
    errors.push({
      path: `${path}.causationEventIds`,
      message: 'causationEventIds must be an array',
    });
  }
  if (!event.correlationId) {
    errors.push({ path: `${path}.correlationId`, message: 'correlationId is required' });
  }
  if (!event.timestamp) {
    errors.push({ path: `${path}.timestamp`, message: 'timestamp is required' });
  }
  if (typeof event.version !== 'number') {
    errors.push({ path: `${path}.version`, message: 'version must be a number' });
  }
  if (typeof event.payload !== 'object' || event.payload === null) {
    errors.push({ path: `${path}.payload`, message: 'payload must be an object' });
  }

  return errors;
}

/**
 * Structural seed validation that does not require a topology — validates
 * PADR-004 envelopes and causation ordering only. Used on load paths where
 * the flow topology is not (yet) available.
 */
export function validateSeedStructure(seed: SimulationSeed): ValidationResult {
  const errors: ValidationError[] = [];
  const seenEventIds = new Set<string>();

  const events = seed.events ?? [];
  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    errors.push(...isValidPadr004Envelope(event, i));

    if (Array.isArray(event.causationEventIds)) {
      for (const causationId of event.causationEventIds) {
        if (!seenEventIds.has(causationId)) {
          errors.push({
            path: `events[${i}].causationEventIds`,
            message: `Causation event "${causationId}" not found among earlier events`,
          });
        }
      }
    }
    seenEventIds.add(event.eventId);
  }

  return errors.length === 0 ? ok() : fail(errors);
}

/** Render a ValidationResult's errors as a short human-readable summary. */
export function formatValidationErrors(result: ValidationResult, maxShown = 3): string {
  const shown = result.errors.slice(0, maxShown).map((e) => `${e.path}: ${e.message}`);
  const more = result.errors.length - shown.length;
  return shown.join('; ') + (more > 0 ? ` (+${more} more)` : '');
}

export function validateSeed(seed: SimulationSeed, topology: SimulationTopology): ValidationResult {
  const errors: ValidationError[] = [];

  // Collect topology node IDs
  const nodeIds = new Set<string>();
  for (const frame of topology.frames) {
    for (const node of frame.nodes) {
      nodeIds.add(node.id);
    }
  }

  // Derive valid event types from topology's eventRouting table
  const topologyEventTypes = new Set<string>(Object.keys(topology.eventRouting));

  // expectedPath node IDs must exist in topology
  for (let i = 0; i < seed.expectedPath.length; i++) {
    const nodeId = seed.expectedPath[i];
    if (!nodeIds.has(nodeId)) {
      errors.push({
        path: `expectedPath[${i}]`,
        message: `Expected path references non-existent node "${nodeId}"`,
      });
    }
  }

  // Validate each event
  const seenEventIds = new Set<string>();

  for (let i = 0; i < seed.events.length; i++) {
    const event = seed.events[i];

    // PADR-004 envelope validation
    const envelopeErrors = isValidPadr004Envelope(event, i);
    errors.push(...envelopeErrors);

    // causationEventIds must reference earlier events in the seed
    if (Array.isArray(event.causationEventIds)) {
      for (const causationId of event.causationEventIds) {
        if (!seenEventIds.has(causationId)) {
          errors.push({
            path: `events[${i}].causationEventIds`,
            message: `Causation event "${causationId}" not found among earlier events`,
          });
        }
      }
    }

    // eventType must exist in topology eventRouting or match Sim* synthetic pattern
    if (event.eventType && !isValidEventType(event.eventType, topologyEventTypes)) {
      errors.push({
        path: `events[${i}].eventType`,
        message: `Unknown event type "${event.eventType}" — not in topology eventRouting and does not match Sim* pattern`,
      });
    }

    seenEventIds.add(event.eventId);
  }

  return errors.length === 0 ? ok() : fail(errors);
}
